import React from 'react';
import { ArrowRight, Globe, Smartphone, Library } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import Section from '../../components/v2/Section';
import V2Hero from '../../components/v2/V2Hero';
import { SecondaryCTA, PrimaryCTA } from '../../components/v2/CTAButton';
import PortfolioGrid from '../../components/v2/PortfolioGrid';
import { HERO } from '../../data/heroImages';

const TYPES = [
  { icon: Library,  t: 'Business website (Authority Hub)', d: 'A dedicated content website that organises your podcast, books, YouTube and articles into one searchable home. From \u00a3495 one-off.' },
  { icon: Globe,    t: 'Service website',          d: 'A clean, trust-building website for your service business. Honest copy, strong information architecture, fast.' },
  { icon: Smartphone, t: 'Simple app or PWA',      d: 'A focused application, members portal, internal tool, customer-facing PWA, scoped tightly to one job done well.' },
];

export default function WebsitesApps() {
  return (
    <main className="font-body text-[#475569] bg-[#F6F8FB]">
      <SEO
        title="Website Design for Coaches & Consultants | Audio & Co."
        description="Project-based business websites and apps for coaches and consultants, built for SEO and AI search. Clear brief, fixed scope, fixed price."
        keywords={['coach website design uk', 'consultant website design', 'website design for small business uk', 'podcast website uk', 'author website uk', 'business website for coaches', 'progressive web app for coaches', 'website designer for coaches']}
        url="https://audioandco.com/websites-apps"
      />

      <V2Hero image={HERO.homeOfficePlants} testId="wa-hero">
        <div className="max-w-3xl">
          <p className="text-[#64748B] font-semibold text-sm uppercase tracking-widest">Project-based engagements</p>
          <h1 className="font-display font-bold text-[#1E2A44] text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight mt-3">
            Website and app builds (project-based).
          </h1>
          <p className="text-lg sm:text-xl text-[#475569] mt-6 leading-relaxed">
            If you need to <Link to="/authority-hub" className="text-[#BC516F] hover:text-[#A7468A] font-semibold">build a business website that attracts premium clients</Link>, a service site, or a simple app, this is scoped
            and quoted separately. Clean briefs, fixed scope, fixed price.
          </p>
          <div className="mt-8">
            <SecondaryCTA to="/contact" size="lg" data-testid="wa-hero-cta">Request a quote <ArrowRight className="w-5 h-5" /></SecondaryCTA>
          </div>
        </div>
      </V2Hero>

      <Section bg="white" testId="wa-types">
        <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight">What we build.</h2>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {TYPES.map((x, i) => {
            const Icon = x.icon;
            return (
              <div key={i} className="bg-[#F6F8FB] border border-[#E2E8F0] rounded-[14px] p-6 sm:p-7">
                <div className="w-11 h-11 rounded-lg bg-white border border-[#E2E8F0] flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-[#BC516F]" />
                </div>
                <h3 className="font-display font-semibold text-[#1E2A44] text-lg">{x.t}</h3>
                <p className="text-[#475569] mt-1.5 leading-relaxed text-[15px]">{x.d}</p>
              </div>
            );
          })}
        </div>
      </Section>

      <Section bg="white" testId="wa-portfolio">
        <PortfolioGrid
          eyebrow="Recent builds"
          heading="Sites and apps we've built."
          subheading="A mix of client work and Jo's own businesses. When we say we build what we sell, this is what we mean."
        />
      </Section>

      <Section bg="bg" testId="wa-how">
        <div className="grid lg:grid-cols-2 gap-10">
          <div>
            <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight">How a project runs.</h2>
            <p className="text-[#475569] mt-4 leading-relaxed">
              We start with a clear brief and a fixed quote. No hourly billing surprises. No scope creep.
              You see progress weekly until launch.
            </p>
          </div>
          <ol className="space-y-3 list-decimal list-inside">
            {[
              'Discovery call, we understand the brief.',
              'Written proposal with scope, timeline, and fixed price.',
              'Build sprints with weekly check-ins.',
              'Launch + handover. Optional Pro Plan to keep it alive.',
            ].map((l, i) => (
              <li key={i} className="bg-white border border-[#E2E8F0] rounded-[14px] p-5 text-[#475569] leading-relaxed marker:text-[#BC516F] marker:font-bold">
                {l}
              </li>
            ))}
          </ol>
        </div>
      </Section>

      <Section bg="white" testId="wa-pro-plan-note">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[#64748B]">
            Most clients pair a project-based build with the <Link to="/pro-plan" className="text-[#BC516F] hover:text-[#A7468A] font-semibold">Pro Plan</Link> so
            the site they’ve invested in keeps growing weekly, instead of sitting still after launch.
          </p>
          <div className="mt-7">
            <SecondaryCTA to="/contact" size="lg" data-testid="wa-final-cta">Request a quote <ArrowRight className="w-5 h-5" /></SecondaryCTA>
          </div>
        </div>
      </Section>
    </main>
  );
}
